<template>
  <MapboxMap @load="onMapLoad" v-if="mapData" map-id="fibmap" :options="mapData" class="h-[80vh]">
    <MapboxDefaultMarker
      v-for="(venue, i) in currentVenue"
      :key="i"
      :marker-id="`marker${i}`"
      :lnglat="venue.geometry.coordinates"
      :options="{ color: '#2CD5C4' }"
    />
  </MapboxMap>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive } from 'vue';
import mapboxgl from 'mapbox-gl';
import { useMapboxMarkerRef } from '#imports';
import type { Feature } from '~/types/MapBlock';
import type { ISbStoryData } from 'storyblok-js-client';

const props = defineProps<{
  longitude: string;
  latitude: string;
  mapStyle?: string;
  showAllVenues?: boolean;
}>();

const translate = useTranslation();
const currentLanguage = useState<string>('language');

const story = useState<ISbStoryData>('story');

const mapCoordinates = ref();
const mapData = ref();
const mapInstance = ref<mapboxgl.Map | null>(null);
const dataset = ref<any>(null);
const currentVenue = ref<any>(null);
const popups: { [key: string]: mapboxgl.Popup } = reactive({});
const currentOpenPopup = ref<string | null>(null);

if (props.latitude && props.longitude) {
  mapCoordinates.value = {
    lng: parseFloat(props.longitude),
    lat: parseFloat(props.latitude),
  };

  mapData.value = {
    style: props.mapStyle,
    center: mapCoordinates.value,
    zoom: 15,
    scrollZoom: false,
  };
}

const onMapLoad = (map: mapboxgl.Map) => {
  mapInstance.value = map;

  const navigationControl = new mapboxgl.NavigationControl({
    showCompass: true,
    visualizePitch: true,
  });
  map.addControl(navigationControl, 'top-right');

  map.once('idle', () => {
    currentVenue.value?.forEach((venue: any, i: number) => {
      const markerId = `marker${i}`;
      const markerRef = useMapboxMarkerRef(markerId);

      if (markerRef.value) {
        markerRef.value.getElement().addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent the map click event from firing
          const goToVenue = translate('action.goToVenue') || 'Visit venue';
          togglePopup(markerId, venue, goToVenue);
        });
        markerRef.value.getElement().innerHTML = `<svg width="43" height="59" viewBox="0 0 43 59" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path opacity="0.3" d="M21.6071 58.4167C24.4169 58.4167 26.6947 57.2778 26.6947 55.8729C26.6947 54.468 24.4169 53.3291 21.6071 53.3291C18.7973 53.3291 16.5195 54.468 16.5195 55.8729C16.5195 57.2778 18.7973 58.4167 21.6071 58.4167Z" fill="black"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M21.6071 0.338867C33.4082 0.338867 42.9749 9.90556 42.9749 21.7067C42.9749 32.8214 34.4887 41.9541 23.6421 42.9788V53.8379C23.6421 54.9618 22.731 55.8729 21.6071 55.8729C20.4831 55.8729 19.572 54.9618 19.572 53.8379V42.9788C8.72546 41.9541 0.239258 32.8214 0.239258 21.7067C0.239258 9.90556 9.80595 0.338867 21.6071 0.338867Z" fill="rgb(244, 152, 192)"/>
        <path d="M38.9048 21.7067C38.9048 12.1534 31.1604 4.40894 21.6071 4.40894C12.0538 4.40894 4.30933 12.1534 4.30933 21.7067C4.30933 31.26 12.0538 39.0044 21.6071 39.0044C31.1604 39.0044 38.9048 31.26 38.9048 21.7067Z" fill="rgb(244, 152, 192)"/>
        <path d="M31.4248 12.6315H12.9446V16.0965H31.4248V12.6315Z" fill="black"/>
        <path d="M23.9172 20.1391H12.9446V23.6041H23.9172V20.1391Z" fill="black"/>
        <path d="M16.4096 27.6467H12.9446V31.1117H16.4096V27.6467Z" fill="black"/>
        </svg>`;
      }
    });
    map.on('click', closeAllPopups);
  });
};

const createPopupContent = (venue: Feature, goToVenue: string) => `
  <div class="bg-white pl-4 pr-12 pt-2 flex flex-row justify-between rounded-t min-w-[240px] w-full h-auto font-primary text-lg">
    <h3 class="w-[100%] md:w-[90%] font-secondary text-xl text-start text-center">${venue.properties.name}</h3>
  </div>
  <p class="bg-white w-full px-4 pt-2 text-start">${venue.properties.address}</p>
  <p class="bg-white w-full px-4 pb-4 text-start ${props.showAllVenues ? '' : 'rounded-b'}" >${venue.properties.location}</p>
  ${
    props.showAllVenues
      ? `
    <button class="bg-primary px-4 py-2 w-full text-center rounded-b focus:outline-none">
      <a href="${currentLanguage.value === 'no' ? venue.properties.webNO : venue.properties.webEN}" class="text-center text-md font-bold focus:outline-none">${goToVenue}</a>
    </button>
  `
      : ''
  }
`;

const closeAllPopups = () => {
  Object.values(popups).forEach((popup) => popup.remove());
  currentOpenPopup.value = null;
};

const togglePopup = (markerId: string, venue: Feature, goToVenue: string) => {
  closeAllPopups();

  if (!popups[markerId]) {
    popups[markerId] = new mapboxgl.Popup({
      closeOnClick: false,
      offset: 45,
    })
      .setLngLat(venue.geometry.coordinates)
      .setHTML(createPopupContent(venue, goToVenue));

    popups[markerId].on('close', () => {
      delete popups[markerId];
      if (currentOpenPopup.value === markerId) {
        currentOpenPopup.value = null;
      }
    });
  }

  if (currentOpenPopup.value !== markerId) {
    popups[markerId].addTo(mapInstance.value);
    currentOpenPopup.value = markerId;
  } else {
    currentOpenPopup.value = null;
  }
};

const fetchDatasetFeatures = async () => {
  try {
    const response = await fetch('/api/arenas');
    const data = await response.json();
    dataset.value = data;

    if (dataset.value && dataset.value.features) {
      currentVenue.value = props.showAllVenues
        ? dataset.value.features
        : dataset.value.features.filter(
            (venue: Feature) => story.value.name == venue.properties.name,
          );
    }
  } catch (error) {
    console.error('Error fetching dataset:', error);
  }
};

onMounted(() => {
  fetchDatasetFeatures();
});
</script>

<style>
.mapboxgl-popup-content {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  width: 100%;
  overflow: hidden !important;
}

.mapboxgl-popup-close-button {
  font-size: 2rem;
  width: fit-content;
  height: fit-content;
  padding: 0.5rem 0.5rem;
}
</style>
